import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, User, Eye, ArrowLeft, Tag, Share2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Function to format plain text content with proper HTML
const formatContent = (text) => {
  if (!text) return '';
  
  // Split by double line breaks to get paragraphs
  const paragraphs = text.split(/\n\n+/);
  
  return paragraphs.map(para => {
    // Check if it's a bullet point line
    if (para.trim().startsWith('•') || para.trim().startsWith('-') || para.trim().startsWith('*')) {
      const items = para.split('\n').filter(line => line.trim());
      const listItems = items.map(item => {
        const cleaned = item.trim().replace(/^[•\-*]\s*/, '');
        return `<li>${cleaned}</li>`;
      }).join('');
      return `<ul style="margin: 1em 0; padding-left: 2em;">${listItems}</ul>`;
    }
    
    // Check if it's a numbered list
    if (/^\d+\./.test(para.trim())) {
      const items = para.split('\n').filter(line => line.trim());
      const listItems = items.map(item => {
        const cleaned = item.trim().replace(/^\d+\.\s*/, '');
        return `<li>${cleaned}</li>`;
      }).join('');
      return `<ol style="margin: 1em 0; padding-left: 2em;">${listItems}</ol>`;
    }
    
    // Regular paragraph - replace single line breaks with <br />
    const withBreaks = para.replace(/\n/g, '<br />');
    return `<p style="margin-bottom: 1.5em;">${withBreaks}</p>`;
  }).join('');
};

const BlogDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [relatedBlogs, setRelatedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchBlog();
    window.scrollTo(0, 0);
  }, [slug]);

  const fetchBlog = async () => {
    try {
      const response = await axios.get(`${API}/api/blogs/${slug}`);
      setBlog(response.data);

      // Fetch related blogs
      const relatedRes = await axios.get(`${API}/api/blogs?category=${response.data.category}`);
      setRelatedBlogs(relatedRes.data.filter(b => b.slug !== slug).slice(0, 3));
    } catch (error) {
      console.error('Failed to fetch blog:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getCategoryColor = (category) => {
    const colors = {
      adventure: 'bg-red-500',
      safety: 'bg-green-500',
      locations: 'bg-blue-500',
      tips: 'bg-yellow-500',
      news: 'bg-purple-500'
    };
    return colors[category] || 'bg-gray-500';
  };

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: blog.excerpt,
        url: window.location.href
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-white text-2xl">Loading...</div>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Blog not found</h2>
          <button
            onClick={() => navigate('/blogs')}
            className="px-6 py-3 bg-yellow-400 text-black font-bold rounded-lg hover:bg-yellow-500"
          >
            Back to Blogs
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <div className="bg-gray-900 border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <button
            onClick={() => navigate('/blogs')}
            className="flex items-center space-x-2 text-gray-400 hover:text-yellow-400 transition-colors mb-4"
          >
            <ArrowLeft size={20} />
            <span>Back to Blogs</span>
          </button>
        </div>
      </div>

      {/* Featured Image */}
      {blog.featuredImage && (
        <div className="relative h-96 overflow-hidden">
          <img
            src={blog.featuredImage}
            alt={blog.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent"></div>
        </div>
      )}

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Category Badge */}
        <div className="mb-6">
          <span className={`px-4 py-2 rounded-full text-sm font-bold text-white ${getCategoryColor(blog.category)}`}>
            {blog.category.toUpperCase()}
          </span>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-black text-white mb-6">{blog.title}</h1>

        {/* Meta Info */}
        <div className="flex flex-wrap items-center gap-6 text-gray-400 mb-8 pb-8 border-b border-gray-800">
          <div className="flex items-center space-x-2">
            <User size={20} />
            <span>{blog.author}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Calendar size={20} />
            <span>{formatDate(blog.publishedAt || blog.createdAt)}</span>
          </div>
          <div className="flex items-center space-x-2">
            <Eye size={20} />
            <span>{blog.views} views</span>
          </div>
          <button
            onClick={handleShare}
            className="flex items-center space-x-2 ml-auto text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            <Share2 size={20} />
            <span>Share</span>
          </button>
        </div>

        {/* Excerpt */}
        {blog.excerpt && (
          <div className="text-xl text-gray-300 mb-8 pb-8 border-b border-gray-800 italic">
            {blog.excerpt}
          </div>
        )}

        {/* Blog Content */}
        <div 
          className="prose prose-invert prose-lg max-w-none blog-content"
          dangerouslySetInnerHTML={{ __html: formatContent(blog.content) }}
          style={{
            color: '#e5e7eb',
            lineHeight: '1.8'
          }}
        />

        {/* Tags */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-800">
            <h3 className="text-white font-bold mb-4">Tags</h3>
            <div className="flex flex-wrap gap-3">
              {blog.tags.map((tag, index) => (
                <span key={index} className="flex items-center space-x-2 px-4 py-2 bg-gray-800 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors">
                  <Tag size={16} />
                  <span>{tag}</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </article>

      {/* Related Blogs */}
      {relatedBlogs.length > 0 && (
        <div className="bg-gray-900 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-black text-white mb-8">Related Articles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {relatedBlogs.map((relatedBlog) => (
                <article
                  key={relatedBlog._id}
                  onClick={() => navigate(`/blog/${relatedBlog.slug}`)}
                  className="group bg-black rounded-xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all cursor-pointer"
                >
                  {relatedBlog.featuredImage ? (
                    <div className="relative h-40 overflow-hidden">
                      <img
                        src={relatedBlog.featuredImage}
                        alt={relatedBlog.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-yellow-500 to-red-600"></div>
                  )}
                  <div className="p-4">
                    <h3 className="text-lg font-bold text-white group-hover:text-yellow-400 transition-colors mb-2">
                      {relatedBlog.title}
                    </h3>
                    {relatedBlog.excerpt && (
                      <p className="text-gray-400 text-sm line-clamp-2">{relatedBlog.excerpt}</p>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDetailPage;